import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { GraduationCap, Plus, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const STATUS_COLORS = { draft: '#F59E0B', submitted: '#3B82F6', approved: '#00F0FF', published: '#10B981', archived: '#555' };
const STATUS_FLOW = { draft: ['submitted'], submitted: ['approved', 'draft'], approved: ['published', 'draft'], published: ['archived'], archived: ['draft'] };
const TYPES = ['learn_to_play', 'setup_guide', 'quick_start', 'strategy'];
const DIFFS = ['beginner', 'intermediate', 'advanced'];

export default function PublisherTutorials() {
  const { publisherId } = useParams();
  const [searchParams] = useSearchParams();
  const gameFilter = searchParams.get('game');
  const [tutorials, setTutorials] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', tutorial_type: 'learn_to_play', difficulty: 'beginner', content: '', game_id: '', game_name: '', steps: [] });
  const [stepForm, setStepForm] = useState({ title: '', content: '' });

  const fetch = () => {
    const url = gameFilter
      ? `${API_URL}/api/publisher-portal/${publisherId}/tutorials?game_id=${gameFilter}`
      : `${API_URL}/api/publisher-portal/${publisherId}/tutorials`;
    axios.get(url, { withCredentials: true }).then(r => setTutorials(r.data || [])).catch(() => {}).finally(() => setLoading(false));
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/games`, { withCredentials: true }).then(r => setGames(r.data || [])).catch(() => {});
  };
  useEffect(fetch, [publisherId, gameFilter]);

  const addStep = () => {
    if (!stepForm.title) return;
    setForm({ ...form, steps: [...form.steps, { order: form.steps.length + 1, ...stepForm }] });
    setStepForm({ title: '', content: '' });
  };

  const removeStep = (idx) => {
    setForm({ ...form, steps: form.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })) });
  };

  const create = async () => {
    if (!form.title) { toast.error('Title required'); return; }
    try {
      await axios.post(`${API_URL}/api/publisher-portal/${publisherId}/tutorials`, form, { withCredentials: true });
      toast.success('Tutorial created as draft'); setShowCreate(false);
      setForm({ title: '', tutorial_type: 'learn_to_play', difficulty: 'beginner', content: '', game_id: '', game_name: '', steps: [] });
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const save = async () => {
    if (!editing) return;
    try {
      await axios.put(`${API_URL}/api/publisher-portal/${publisherId}/tutorials/${editing.id}`, form, { withCredentials: true });
      toast.success('Tutorial updated'); setEditing(null); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/publisher-portal/${publisherId}/tutorials/${id}/status`, { status }, { withCredentials: true });
      toast.success(`Status: ${status}`); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startEdit = (t) => {
    setEditing(t); setShowCreate(false);
    setForm({ title: t.title, tutorial_type: t.tutorial_type || 'learn_to_play', difficulty: t.difficulty || 'beginner', content: t.content || '', game_id: t.game_id || '', game_name: t.game_name || '', steps: t.steps || [] });
  };
  const cancelEdit = () => { setEditing(null); setForm({ title: '', tutorial_type: 'learn_to_play', difficulty: 'beginner', content: '', game_id: '', game_name: '', steps: [] }); };

  const inputCls = "w-full bg-white/[0.03] border border-white/[0.08] rounded-lg px-3 py-2 text-white text-sm focus:border-[#10B981]/50 focus:outline-none placeholder-[#444]";
  const diffColors = { beginner: '#10B981', intermediate: '#F59E0B', advanced: '#EF4444' };

  const onGameChange = (gid) => {
    const game = games.find(g => g.id === gid);
    setForm({ ...form, game_id: gid, game_name: game?.name || '' });
  };

  const FormView = ({ onSubmit, label }) => (
    <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5 mb-6 space-y-4">
      <input className={inputCls} placeholder="Tutorial title" value={form.title}
        onChange={e => setForm({ ...form, title: e.target.value })} data-testid="tutorial-title-input" />
      <div className="grid grid-cols-3 gap-3">
        <select className={inputCls} value={form.game_id} onChange={e => onGameChange(e.target.value)}>
          <option value="">Select game...</option>
          {games.map(g => <option key={g.id || g.slug} value={g.id}>{g.name}</option>)}
        </select>
        <select className={inputCls} value={form.tutorial_type} onChange={e => setForm({ ...form, tutorial_type: e.target.value })}>
          {TYPES.map(t => <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
        </select>
        <select className={inputCls} value={form.difficulty} onChange={e => setForm({ ...form, difficulty: e.target.value })}>
          {DIFFS.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>)}
        </select>
      </div>
      <textarea className={inputCls + " h-24 resize-y"} placeholder="Overview content..."
        value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} />

      {/* Steps Builder */}
      <div>
        <p className="text-sm text-white font-semibold mb-2">Steps ({form.steps.length})</p>
        {form.steps.map((step, i) => (
          <div key={i} className="flex items-start gap-2 mb-2 bg-white/[0.02] border border-white/[0.04] rounded-lg p-3">
            <span className="text-xs text-[#F59E0B] font-bold w-6 pt-0.5">{step.order}.</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium">{step.title}</p>
              <p className="text-xs text-[#777] mt-0.5">{step.content}</p>
            </div>
            <button onClick={() => removeStep(i)} className="text-[#555] hover:text-[#EF4444] text-xs">Remove</button>
          </div>
        ))}
        <div className="flex gap-2 mt-2">
          <input className={inputCls + " flex-1"} placeholder="Step title" value={stepForm.title}
            onChange={e => setStepForm({ ...stepForm, title: e.target.value })} data-testid="step-title-input" />
          <input className={inputCls + " flex-1"} placeholder="Step content" value={stepForm.content}
            onChange={e => setStepForm({ ...stepForm, content: e.target.value })} />
          <button onClick={addStep} className="px-3 py-2 rounded-lg bg-white/5 text-[#10B981] text-sm font-medium hover:bg-white/10 transition-colors"
            data-testid="add-step-btn">Add</button>
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-2">
        <button onClick={cancelEdit} className="px-4 py-2 text-sm text-[#888]">Cancel</button>
        <button onClick={onSubmit} className="px-5 py-2 rounded-lg bg-[#10B981] text-black font-semibold text-sm" data-testid="tutorial-submit-btn">{label}</button>
      </div>
    </div>
  );

  return (
    <div data-testid="publisher-tutorials-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Tutorials</h1>
          <p className="text-sm text-[#666] mt-1">{tutorials.length} tutorials</p>
        </div>
        {!editing && (
          <button onClick={() => { setShowCreate(!showCreate); setEditing(null); }}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#10B981] text-black font-semibold text-sm"
            data-testid="create-tutorial-btn">
            <Plus size={16} weight="bold" /> New Tutorial
          </button>
        )}
      </div>

      {showCreate && !editing && <FormView onSubmit={create} label="Create Draft" />}
      {editing && <FormView onSubmit={save} label="Save Changes" />}

      <div className="space-y-3">
        {loading ? [...Array(2)].map((_, i) => <div key={i} className="h-24 bg-white/5 rounded-xl animate-pulse" />) :
        tutorials.length === 0 ? (
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-10 text-center">
            <GraduationCap size={44} className="mx-auto text-[#333] mb-4" />
            <p className="text-white font-medium text-lg mb-1">No Tutorials Yet</p>
            <p className="text-sm text-[#555]">Create learn-to-play and setup guides.</p>
          </div>
        ) : tutorials.map(t => {
          const nextStatuses = STATUS_FLOW[t.status] || [];
          return (
            <div key={t.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-3">
                  <GraduationCap size={20} weight="duotone" className="text-[#10B981]" />
                  <div>
                    <p className="text-white font-semibold">{t.title}</p>
                    <div className="flex items-center gap-2 text-[11px] text-[#555]">
                      {t.game_name && <span>{t.game_name}</span>}
                      <span className="capitalize" style={{ color: diffColors[t.difficulty] || '#888' }}>{t.difficulty}</span>
                      <span className="capitalize">{(t.tutorial_type || '').replace(/_/g, ' ')}</span>
                      {t.steps?.length > 0 && <span>· {t.steps.length} steps</span>}
                    </div>
                  </div>
                </div>
                <span className="text-[11px] px-2.5 py-1 rounded-full font-medium"
                  style={{ backgroundColor: `${STATUS_COLORS[t.status] || '#555'}15`, color: STATUS_COLORS[t.status] || '#555' }}>
                  {t.status}
                </span>
              </div>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/[0.04]">
                <button onClick={() => startEdit(t)} className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-lg bg-white/5 text-[#888] hover:text-white transition-colors">
                  <PencilSimple size={12} /> Edit
                </button>
                {nextStatuses.map(s => (
                  <button key={s} onClick={() => changeStatus(t.id, s)}
                    className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
                    style={{ backgroundColor: `${STATUS_COLORS[s]}12`, color: STATUS_COLORS[s] }}>
                    {s === 'published' ? 'Publish' : s === 'submitted' ? 'Submit' : s === 'approved' ? 'Approve' : s === 'archived' ? 'Archive' : 'Revert'}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
